import { FC, useState } from "react";
import { useDispatch } from "react-redux";
import { BookingRequestDetails } from "../../redux/reducers/Properties/PropertiesSliceTypes";
import { AppDispatch } from "redux/store";
import {
  getBookingDetails,
  sendBookingInvoice,
} from "../../redux/reducers/Properties/PropertiesSlice";
import { currencySymbol } from "shared/CurrencySymbol";
import moment from "moment";
import { Modal } from "@mantine/core";
import ExtraExpenseDiscount from "./ExtraExpense";

interface InvoiceProps {
  bookingRequestDetails: BookingRequestDetails | null;
  closeInvoice: () => void;
  callback?: () => void;
  fetchMessages?: (pge: number) => Promise<void>;
}

const Invoice: FC<InvoiceProps> = ({
  bookingRequestDetails,
  closeInvoice,
  callback,
  fetchMessages,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [expenses, setExpenses] = useState<{ name: string; value: number }[]>(
    []
  );
  const [discounts, setDiscounts] = useState<number[]>([]);
  const [expenseName, setExpenseName] = useState("");
  const [expenseValue, setExpenseValue] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const addExpense = () => {
    if (expenseName && expenseValue) {
      setExpenses([
        ...expenses,
        { name: expenseName, value: parseFloat(expenseValue) },
      ]);
      setExpenseName("");
      setExpenseValue("");
    }
  };

  const addDiscount = () => {
    if (discountValue) {
      setDiscounts([...discounts, parseFloat(discountValue)]);
      setDiscountValue("");
    }
  };

  const totalExpenses = expenses.reduce((sum, exp) => sum + exp.value, 0);
  const totalDiscounts = discounts.reduce((sum, disc) => sum + disc, 0);
  const total =
    bookingRequestDetails?.total &&
    Number(bookingRequestDetails?.total) + (totalExpenses - totalDiscounts);

  const removeDiscount = (index: any) => {
    const updatedDiscounts = [...discounts];
    updatedDiscounts.splice(index, 1);
    setDiscounts(updatedDiscounts);
  };

  const removeExpense = (index: any) => {
    const updatedExpenses = [...expenses];
    updatedExpenses.splice(index, 1);
    setExpenses(updatedExpenses);
  };

  const sendInvoiceHandler = async () => {
    try {
      setIsLoading(true);

      const payloadData = {
        booking_id: bookingRequestDetails?.id,
        booking: {
          status: "invoice_sent",
          invoice_attributes: {
            status: "sent",
            billing_type: "one time",
            invoice_type: "Reservation fee",
            expenses_attributes: expenses?.map((x) => ({
              category: x.name,
              amount: x.value,
            })),
            discounts_attributes: discounts?.map((y) => ({
              description: "Seasonal discount",
              amount: y,
            })),
          },
        },
      };

      const action = await dispatch(sendBookingInvoice(payloadData));
      if (
        sendBookingInvoice.fulfilled.match(action) &&
        action.payload?.success
      ) {
        if (callback) {
          callback();
        }
        await dispatch(getBookingDetails(bookingRequestDetails?.id));
        if (fetchMessages) {
          await fetchMessages(1);
        }
      } else {
        console.error(
          "Failed to send booking invoice",
          action?.payload?.message || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Error in sendInvoiceHandler:", error);
    } finally {
      setIsLoading(false);
      closeInvoice();
    }
  };

  return (
    <Modal
      opened={true}
      onClose={closeInvoice}
      title="Create Invoice"
      size="xl"
      centered
      overlayProps={{
        backgroundOpacity: 0.55,
        blur: 3,
      }}
    >
      {bookingRequestDetails && (
        <div>
          {/* Mobile Extra Expense/Discount Section */}
          <div className="md:hidden px-3 py-2 bg-gray-50 border-b">
            <ExtraExpenseDiscount
              expenseName={expenseName}
              setExpenseName={setExpenseName}
              expenseValue={expenseValue}
              setExpenseValue={setExpenseValue}
              addExpense={addExpense}
              discountValue={discountValue}
              setDiscountValue={setDiscountValue}
              addDiscount={addDiscount}
              currencySymbol={currencySymbol}
            />
          </div>

          <div className="flex flex-col md:flex-row p-3 md:p-4 gap-3 md:gap-4">
            {/* Invoice Section */}
            <div className="w-full md:w-2/3 p-3 md:p-5 border border-gray-200 rounded-lg shadow-sm bg-white">
              {/* Booking Details Header */}
              <div className="mb-3 md:mb-4 pb-3 md:pb-4 border-b-2 border-[#f5694b]/20">
                <h3 className="text-sm md:text-base font-semibold text-gray-800 mb-2">
                  Booking Details
                </h3>
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-xs font-medium text-gray-600">
                      Period
                    </span>
                    <span className="text-xs text-gray-800 text-right">
                      {moment.utc(bookingRequestDetails?.check_in_date).format("MMM DD, YYYY")}
                       {moment.utc(bookingRequestDetails?.check_out_date).format("MMM DD, YYYY")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-gray-600">
                      Duration
                    </span>
                    <span className="text-xs text-gray-800">
                      {bookingRequestDetails?.no_of_months || "1"}{" "}
                      {Number(bookingRequestDetails?.no_of_months) === 1
                        ? "Month"
                        : "Months"}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-gray-600">
                      Guests
                    </span>
                    <span className="text-xs text-gray-800">
                      {bookingRequestDetails?.number_of_guests}
                    </span>
                  </div>
                </div>
              </div>

              {/* Invoice Items */}
              <div className="mb-3">
                <div className="overflow-x-auto -mx-3 md:mx-0">
                  <table className="w-full min-w-full">
                    <thead>
                      <tr className="border-b-2 border-[#f5694b]/20">
                        <th className="text-left font-semibold text-gray-700 pb-2 text-xs px-3 md:px-0">
                          Description
                        </th>
                        <th className="text-right font-semibold text-gray-700 pb-2 text-xs px-2 md:px-0">
                          Amount
                        </th>
                        <th className="w-6 px-2 md:px-0"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      <tr className="text-xs">
                        <td className="py-2 text-gray-700 px-3 md:px-0">
                          First Month's Rent
                        </td>
                        <td className="py-2 text-right text-gray-800 font-medium px-2 md:px-0 whitespace-nowrap">
                          {`${currencySymbol()} ${
                            Number(bookingRequestDetails?.item_total)?.toFixed(
                              2
                            ) || "N/A"
                          }`}
                        </td>
                        <td className="px-2 md:px-0"></td>
                      </tr>

                      {Number(bookingRequestDetails?.service_tax) > 0 && (
                        <tr className="text-xs">
                          <td className="py-2 text-gray-700 px-3 md:px-0">
                            Service Fee
                          </td>
                          <td className="py-2 text-right text-gray-800 font-medium px-2 md:px-0 whitespace-nowrap">
                            {`${currencySymbol()} ${
                              Number(
                                bookingRequestDetails?.service_tax
                              )?.toFixed(2) || "N/A"
                            }`}
                          </td>
                          <td className="px-2 md:px-0"></td>
                        </tr>
                      )}

                      {Number(bookingRequestDetails?.extra_guest_amount) >
                        0 && (
                        <tr className="text-xs">
                          <td className="py-2 text-gray-700 px-3 md:px-0">
                            Extra Guests
                          </td>
                          <td className="py-2 text-right text-gray-800 font-medium px-2 md:px-0 whitespace-nowrap">
                            {`${currencySymbol()} ${
                              Number(
                                bookingRequestDetails?.extra_guest_amount
                              )?.toFixed(2) || "N/A"
                            }`}
                          </td>
                          <td className="px-2 md:px-0"></td>
                        </tr>
                      )}

                      {Number(bookingRequestDetails?.tax_total) > 0 && (
                        <tr className="text-xs">
                          <td className="py-2 text-gray-700 px-3 md:px-0">
                            Tax Total
                          </td>
                          <td className="py-2 text-right text-gray-800 font-medium px-2 md:px-0 whitespace-nowrap">
                            {`${currencySymbol()} ${
                              Number(bookingRequestDetails?.tax_total)?.toFixed(
                                2
                              ) || "N/A"
                            }`}
                          </td>
                          <td className="px-2 md:px-0"></td>
                        </tr>
                      )}

                      {/* Custom Expenses */}
                      {expenses.map((expense, index) => (
                        <tr
                          key={`expense-${index}`}
                          className="text-xs bg-[#f5694b]/5"
                        >
                          <td
                            className="py-2 text-gray-700 max-w-[100px] md:max-w-[200px] truncate px-3 md:px-0"
                            title={expense.name}
                          >
                            {expense.name}
                          </td>
                          <td className="py-2 text-right text-gray-800 font-medium px-2 md:px-0 whitespace-nowrap">
                            {`${currencySymbol()} ${
                              Number(expense.value)?.toFixed(2) || "N/A"
                            }`}
                          </td>
                          <td className="py-2 text-center px-2 md:px-0">
                            <button
                              onClick={() => removeExpense(index)}
                              className="text-gray-400 hover:text-[#f5694b] transition-colors text-base"
                              title="Remove"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}

                      {/* Discounts - shown as negative amounts */}
                      {discounts.map((discount, index) => (
                        <tr
                          key={`discount-${index}`}
                          className="text-xs bg-green-50/30"
                        >
                          <td className="py-2 text-gray-700 px-3 md:px-0">
                            Discount
                          </td>
                          <td className="py-2 text-right text-green-600 font-medium px-2 md:px-0 whitespace-nowrap">
                            -
                            {`${currencySymbol()} ${
                              Number(discount)?.toFixed(2) || "N/A"
                            }`}
                          </td>
                          <td className="py-2 text-center px-2 md:px-0">
                            <button
                              onClick={() => removeDiscount(index)}
                              className="text-gray-400 hover:text-red-500 transition-colors text-base"
                              title="Remove"
                            >
                              ×
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Total */}
                <div className="mt-3 pt-3 border-t-2 border-[#f5694b]/30 px-3 md:px-0">
                  <div className="flex justify-between items-center">
                    <span className="text-sm md:text-base font-bold text-gray-800">
                      Total
                    </span>
                    <span className="text-base md:text-lg font-bold text-[#f5694b]">
                      {`${currencySymbol()} ${
                        Number(total)?.toFixed(2) || "N/A"
                      }`}
                    </span>
                  </div>
                </div>
              </div>

              {/* Send Invoice Button */}
              <button
                onClick={sendInvoiceHandler}
                className={`mt-3 px-4 py-2 rounded-lg w-full font-medium text-white transition-all duration-200 text-sm ${
                  isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-[#f5694b] hover:bg-[#e55838] active:bg-[#d54828] shadow-sm hover:shadow"
                }`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin border-t-2 border-white border-solid rounded-full w-4 h-4 mr-2"></div>
                    Sending...
                  </div>
                ) : (
                  "Send Invoice"
                )}
              </button>
            </div>

            {/* Desktop Side Panel - Add Expense/Discount */}
            <div className="hidden md:block w-72">
              <div className="space-y-3">
                {/* Add Expense Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-[#f5694b] mb-3">
                    Add Expense
                  </h3>
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={expenseName}
                      onChange={(e) => setExpenseName(e.target.value)}
                      placeholder="Expense name"
                      className="border border-gray-300 p-2 w-full rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f5694b] focus:border-transparent transition-all text-xs"
                    />
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 text-gray-500 text-xs">
                          {currencySymbol()}
                        </span>
                        <input
                          type="text"
                          value={expenseValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (/^\d*\.?\d*$/.test(value)) {
                              setExpenseValue(e.target.value);
                            }
                          }}
                          placeholder="Amount"
                          className="border border-gray-300 p-2 w-full pl-7 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f5694b] focus:border-transparent transition-all text-xs"
                          min="0"
                        />
                      </div>
                      <button
                        onClick={addExpense}
                        disabled={!expenseName || !expenseValue}
                        className="px-4 py-2 bg-[#f5694b] text-white rounded-lg font-medium text-xs hover:bg-[#e55838] active:bg-[#d54828] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Add Discount Card */}
                <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
                  <h3 className="text-sm font-semibold text-gray-800 mb-3">
                    Add Discount
                  </h3>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-2.5 text-gray-500 text-xs">
                        {currencySymbol()}
                      </span>
                      <input
                        type="text"
                        value={discountValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d*\.?\d*$/.test(value)) {
                            setDiscountValue(e.target.value);
                          }
                        }}
                        placeholder="Amount"
                        className="border border-gray-300 p-2 w-full pl-7 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-xs"
                        min="0"
                      />
                    </div>
                    <button
                      onClick={addDiscount}
                      disabled={!discountValue}
                      className="px-4 py-2 bg-green-500 text-white rounded-lg font-medium text-xs hover:bg-green-600 active:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      Add
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default Invoice;
